// Gates on `Style._loaded` (stylesheet parsed), not the public `isStyleLoaded()`, which is also
// false whenever tiles are in flight -- gating on that skips the add and loses the layer for good.
// The getter is a fallback in case a mapbox upgrade renames the flag.
const styleReady = (map) => !!(map?.style?._loaded ?? map?.isStyleLoaded());

// Adds must happen from an effect. mapbox fires `styledata` mid-render-pass, after
// `layer.recalculate()`, so a layer added from that handler hits `drawFill` with `paint` still
// undefined -- the "Cannot read properties of undefined (reading 'get')" crash on Add Subregion.
// `style.load` fires from `Style._load`, outside the pass. Returns an unsubscribe.
export const whenStyleReady = (map, fn) => {
    if (styleReady(map)) {
        fn();
        return () => {};
    }

    map.once('style.load', fn);
    return () => map.off('style.load', fn);
};

// Structural equality for mapbox `paint` / `filter` values. Style short-circuits an unchanged
// value, but `Map.setFilter` / `setPaintProperty` call `_update(true)` regardless and schedule a
// repaint -- so redundant calls have to be stopped one level up.
export const sameStyleValue = (a, b) => {
    if (a === b) return true;
    if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;

    if (Array.isArray(a) || Array.isArray(b)) {
        if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
        return a.every((value, i) => sameStyleValue(value, b[i]));
    }

    const keys = Object.keys(a);
    if (keys.length !== Object.keys(b).length) return false;
    return keys.every((key) => sameStyleValue(a[key], b[key]));
};
